import { defineComponent, onMounted, ref, toRef } from "vue";
import type { CombinedFieldProps } from "../../types/skyward/treebrowse";
import { DefaultVueProp, VueEmit, VueProp } from "../core/vueHelpers";
import * as Classes from "./classes";
import { Emits, recordDoubleClick, recordMounted, recordSelected } from "./events";

export type BaseFieldDefinition<TField extends object, TFormatted extends object> =
    {
        name: string,
        additionalRowAttributes: (field: TField) => object,
        nameCell: (field: TField) => JSX.Element,
        parametersCell?: (field: TField) => JSX.Element;
        typeIcon: (field: TField) => string;
        fieldKey: (field: TField) => string;
        formatField: (field: TField) => TFormatted
    }

export function defineBaseFieldComponent<TField extends CombinedFieldProps, TFormatted extends object>(fieldDefinition: BaseFieldDefinition<TField, TFormatted>)
{
    const component = defineComponent(
        {
            name: fieldDefinition.name,
            props:
            {
                field: VueProp<TField>(),
                originalField: VueProp<TField>(),
                selected: DefaultVueProp(false),
            },
            emits:
            {
                [recordMounted]: VueEmit<Emits["recordMounted"]>(),
                [recordSelected]: VueEmit<Emits["recordSelected"]>(),
                [recordDoubleClick]: VueEmit<Emits["recordDoubleClick"]>(),
            },
            setup(wrongTypeProps, context)
            {
                const props = wrongTypeProps as unknown as { field: TField; originalField: TField; selected: boolean; };
                const rowRef = ref<HTMLTableRowElement>();
                onMounted(() =>
                {
                    context.emit(recordMounted, rowRef.value!);
                });

                context.expose(
                {
                    field: toRef(props, "field"),
                    originalField: toRef(props, "originalField"),
                    fieldKey: () => fieldDefinition.fieldKey(props.field),
                    formatField: () => fieldDefinition.formatField(props.field),
                });

                return () =>
                    <tr
                        class={{ [Classes.selected]: props.selected }}
                        onClick={() => context.emit(recordSelected, props.field)}
                        onDblclick={() => context.emit(recordDoubleClick, props.field)}
                        ref={rowRef}
                        style={{ fontWeight: props.field !== props.originalField ? "bold" : "normal" }}
                        {...fieldDefinition.additionalRowAttributes(props.field)}
                    >
                        {context.slots.default!(
                            {
                                field: props.field,
                                nameCell: fieldDefinition.nameCell(props.field),
                                parametersCell: fieldDefinition.parametersCell?.(props.field),
                                typeIcon: fieldDefinition.typeIcon(props.field),
                            }
                        )}
                    </tr>;
            },
            fieldKey: fieldDefinition.fieldKey,
        }
    );

    type StaticMethods =
        {
            fieldKey: typeof fieldDefinition.fieldKey;
        }
    type InstanceMethods =
        {
            fieldKey: () => string,
            field: TField,
            originalField: TField,
            formatField: () => TFormatted
        };
    type ConstructorMethod =
        {
            new(...args: any[]): InstanceType<typeof component> & InstanceMethods;
        };

    return component as (typeof component) & StaticMethods & ConstructorMethod;
}
